/**
 * Las hojas del archivo de Excel (Req. 28; design.md, 10.10).
 *
 * Este módulo solo arma las filas: no sabe de Excel ni de la base. Devuelve
 * números de verdad (no textos) para que se puedan sumar en la hoja, con la
 * unidad en el encabezado de cada columna, y las fechas como texto
 * 'AAAA-MM-DD' (Req. 28.2), que es lo que no se desordena al abrir el archivo
 * en otra computadora.
 *
 * Reglas:
 * - Un dato que no existe queda en blanco, no en cero: un macro sin registrar
 *   no es "0 g".
 * - Los pesos y las medidas van en la unidad preferida, y aparte lo que se
 *   capturó tal cual (Req. 5.4): así se ve lo que se escribió y lo comparable.
 * - Las cargas en placas no se convierten: el número de placa va en su
 *   columna y la de carga queda en blanco.
 * - Sí y No en lugar de TRUE y FALSE: el archivo se lee en español.
 *
 * El Excel NO es un respaldo y no se puede volver a importar (Req. 28.3): es
 * para mirar los datos, no para moverlos.
 */

import type {
  CalidadDato,
  Day,
  EstadoCoccion,
  Exercise,
  Fast,
  MealEntry,
  Measurement,
  Plan,
  Scale,
  Stage,
  TipoEtapa,
  TipoSerie,
  TiempoComida,
  WeighIn,
  Workout,
  WorkoutSet,
} from '@/db/types';
import type { FechaLocal } from './dates';
import { repsDeSerie } from './entrenamiento';
import type { MetasDelDia } from './etapas';
import { ETIQUETAS_MODALIDAD, modalidadDe } from './modalidad';
import { redondear } from './numbers';
import type { Totales } from './nutricion';
import { fechaLocalDe, horaLocalDe } from './time';
import { desdeCm, desdeKg, type UnidadLongitud, type UnidadPeso } from './units';

export type Celda = string | number | null;

export interface HojaDeExcel {
  /** Nombre de la pestaña; Excel no admite más de 31 caracteres. */
  nombre: string;
  /** La primera fila son los encabezados. */
  filas: Celda[][];
}

export interface UnidadesDeExcel {
  peso: UnidadPeso;
  longitud: UnidadLongitud;
}

const si = (v: boolean) => (v ? 'Sí' : 'No');
/** Un peso en la unidad preferida, con dos decimales; null se queda en blanco. */
const peso = (kg: number | null | undefined, unidad: UnidadPeso): number | null =>
  kg === null || kg === undefined || !Number.isFinite(kg) ? null : redondear(desdeKg(kg, unidad), 2);
const numero = (v: number | null | undefined): number | null =>
  v === null || v === undefined || !Number.isFinite(v) ? null : redondear(v, 2);
const texto = (v: string | null | undefined): string | null => (v === null || v === undefined || v === '' ? null : v);
/** La hora local de un instante ISO; null si no se puede leer. */
const horaDe = (iso: string | null): string | null => {
  if (iso === null || Number.isNaN(Date.parse(iso))) return null;
  return horaLocalDe(iso);
};
/** "2026-10-29 20:10", para un instante. */
const fechaYHora = (iso: string | null): string | null => {
  if (iso === null || Number.isNaN(Date.parse(iso))) return null;
  return `${fechaLocalDe(iso)} ${horaLocalDe(iso)}`;
};

export const ETIQUETA_TIPO_SERIE: Readonly<Record<TipoSerie, string>> = {
  calentamiento: 'Calentamiento',
  normal: 'Normal',
  dropset: 'Dropset',
  fallo: 'Al fallo',
};

export const ETIQUETA_TIEMPO: Readonly<Record<TiempoComida, string>> = {
  desayuno: 'Desayuno',
  comida: 'Comida',
  cena: 'Cena',
  colacion: 'Colación',
};

export const ETIQUETA_TIPO_ETAPA: Readonly<Record<TipoEtapa, string>> = {
  deficit: 'Déficit',
  mantenimiento: 'Mantenimiento',
  superavit: 'Superávit',
};

const ETIQUETA_ESTADO: Readonly<Record<EstadoCoccion, string>> = { crudo: 'Crudo', cocido: 'Cocido' };

const ETIQUETA_CALIDAD: Readonly<Record<CalidadDato, string>> = {
  etiqueta: 'Etiqueta',
  referencia: 'Referencia',
  aproximada: 'Aproximación',
  estimada: 'Estimación',
};

const ETIQUETA_ORIGEN_AYUNO: Readonly<Record<Fast['origen'], string>> = {
  temporizador: 'Temporizador',
  manual: 'Anotado a mano',
  comidas: 'Calculado con tus comidas',
  correccion: 'Corregido',
};

const ETIQUETA_MEDIDA: Readonly<Record<Measurement['tipo'], string>> = {
  cintura: 'Cintura',
  cadera: 'Cadera',
  pecho: 'Pecho',
  brazo: 'Brazo',
  muslo: 'Muslo',
  pantorrilla: 'Pantorrilla',
  cuello: 'Cuello',
  grasa: 'Grasa corporal',
};

// ---------------------------------------------------------------------------
// Entrenamientos: una fila por serie (Req. 28.1)
// ---------------------------------------------------------------------------

export interface SerieParaExcel {
  serie: WorkoutSet;
  workout: Workout;
  ejercicio: Exercise | undefined;
}

export function hojaDeEntrenamientos(series: readonly SerieParaExcel[], unidades: UnidadesDeExcel): HojaDeExcel {
  const u = unidades.peso;
  const filas: Celda[][] = [
    [
      'Fecha',
      'Hora',
      'Entrenamiento',
      'Dónde',
      'Ejercicio',
      'Serie',
      'Tipo de serie',
      `Carga (${u})`,
      'Lo que escribí',
      'Unidad',
      'Reps',
      'Reps izq.',
      'Reps der.',
      'Tiempo (s)',
      'Distancia (m)',
      'RIR',
      'Hecha',
      `Volumen (${u})`,
    ],
  ];
  for (const { serie, workout, ejercicio } of series) {
    const cuenta = serie.hecha && serie.tipoSerie !== 'calentamiento';
    filas.push([
      workout.fecha,
      horaDe(serie.hechaEn) ?? horaDe(workout.inicio),
      workout.nombre,
      ETIQUETAS_MODALIDAD[modalidadDe(workout)],
      ejercicio?.nombre ?? 'Ejercicio que ya no existe',
      serie.orden + 1,
      ETIQUETA_TIPO_SERIE[serie.tipoSerie],
      peso(serie.pesoKg, u),
      numero(serie.pesoCapturado?.valor),
      serie.pesoCapturado?.unidad ?? null,
      numero(serie.reps),
      numero(serie.repsIzq),
      numero(serie.repsDer),
      numero(serie.duracionSeg),
      numero(serie.distanciaM),
      numero(serie.rir),
      si(serie.hecha),
      cuenta && serie.pesoKg !== null ? peso(serie.pesoKg * repsDeSerie(serie), u) : null,
    ]);
  }
  return { nombre: 'Entrenamientos', filas };
}

// ---------------------------------------------------------------------------
// Pesajes y medidas (Req. 28.1, 28.2)
// ---------------------------------------------------------------------------

export interface PesajeParaExcel {
  pesaje: WeighIn;
  bascula: Scale | undefined;
}

export function hojaDePesajes(pesajes: readonly PesajeParaExcel[], unidades: UnidadesDeExcel): HojaDeExcel {
  const u = unidades.peso;
  const filas: Celda[][] = [['Fecha', 'Hora', `Peso (${u})`, 'Lo que escribí', 'Unidad', 'Báscula', 'Nota']];
  for (const { pesaje, bascula } of pesajes) {
    filas.push([
      pesaje.fecha,
      pesaje.hora,
      peso(pesaje.pesoKg, u),
      numero(pesaje.capturado?.valor),
      pesaje.capturado?.unidad ?? null,
      bascula?.nombre ?? null,
      texto(pesaje.nota),
    ]);
  }
  return { nombre: 'Pesajes', filas };
}

export function hojaDeMedidas(medidas: readonly Measurement[], unidades: UnidadesDeExcel): HojaDeExcel {
  const filas: Celda[][] = [['Fecha', 'Medida', 'Valor', 'Unidad', 'Lo que escribí', 'Unidad escrita', 'Nota']];
  for (const m of medidas) {
    const esGrasa = m.tipo === 'grasa';
    filas.push([
      m.fecha,
      ETIQUETA_MEDIDA[m.tipo] ?? m.tipo,
      esGrasa ? numero(m.valor) : numero(desdeCm(m.valor, unidades.longitud)),
      esGrasa ? '%' : unidades.longitud,
      numero(m.capturado?.valor),
      m.capturado?.unidad ?? null,
      texto(m.nota),
    ]);
  }
  return { nombre: 'Medidas', filas };
}

// ---------------------------------------------------------------------------
// Comidas y totales por día (Req. 28.1)
// ---------------------------------------------------------------------------

export function hojaDeComidas(comidas: readonly MealEntry[]): HojaDeExcel {
  const filas: Celda[][] = [
    [
      'Fecha',
      'Hora',
      'Tiempo',
      'Alimento',
      'Cantidad',
      'Porción',
      'Gramos',
      'kcal',
      'Proteína (g)',
      'Carbohidratos (g)',
      'Grasa (g)',
      'Fibra (g)',
      'Rompe el ayuno',
      'Crudo o cocido',
      'De dónde salen los valores',
      'Nota',
    ],
  ];
  for (const c of comidas) {
    filas.push([
      c.fecha,
      c.hora,
      ETIQUETA_TIEMPO[c.tiempo] ?? c.tiempo,
      c.nombre,
      numero(c.cantidad),
      texto(c.porcion),
      numero(c.gramos),
      numero(c.valores.kcal),
      numero(c.valores.proteinaG),
      numero(c.valores.carbohidratosG),
      numero(c.valores.grasaG),
      numero(c.valores.fibraG),
      si(c.rompeAyuno),
      c.estado == null ? null : ETIQUETA_ESTADO[c.estado],
      c.calidad == null ? null : ETIQUETA_CALIDAD[c.calidad],
      texto(c.nota),
    ]);
  }
  return { nombre: 'Comidas', filas };
}

export interface DiaParaExcel {
  fecha: FechaLocal;
  totales: Totales;
  dia: Day | undefined;
  metas: MetasDelDia;
  /** La etapa que cubría ese día, si había plan. */
  etapa: TipoEtapa | null;
}

export function hojaDeTotales(dias: readonly DiaParaExcel[]): HojaDeExcel {
  const filas: Celda[][] = [
    [
      'Fecha',
      'Registros',
      'kcal',
      'Proteína (g)',
      'Carbohidratos (g)',
      'Grasa (g)',
      'Fibra (g)',
      'Datos que faltan',
      'Registro incompleto',
      'Etapa',
      'Meta de kcal',
      'Meta de proteína (g)',
      'Meta de carbohidratos (g)',
      'Meta de grasa (g)',
      'Meta de ayuno (h)',
      'Nota del día',
    ],
  ];
  for (const d of dias) {
    const { totales: t, metas } = d;
    /**
     * Un macro que NINGÚN registro trae queda en blanco: no es "0 g". Si solo
     * algunos lo traen, va la suma de esos y la columna "Datos que faltan"
     * dice cuáles están incompletos, como el asterisco de la app.
     */
    const macro = (campo: 'proteinaG' | 'carbohidratosG' | 'grasaG' | 'fibraG'): number | null =>
      t.registros > 0 && t.sinDato[campo] === t.registros ? null : numero(t[campo]);
    const faltan = (
      [
        ['proteína', 'proteinaG'],
        ['carbohidratos', 'carbohidratosG'],
        ['grasa', 'grasaG'],
        ['fibra', 'fibraG'],
      ] as const
    )
      .filter(([, campo]) => t.sinDato[campo] > 0)
      .map(([nombre]) => nombre);
    filas.push([
      d.fecha,
      t.registros,
      numero(t.kcal),
      macro('proteinaG'),
      macro('carbohidratosG'),
      macro('grasaG'),
      macro('fibraG'),
      faltan.length === 0 ? null : faltan.join(', '),
      si(d.dia?.registroIncompleto === true),
      d.etapa === null ? null : ETIQUETA_TIPO_ETAPA[d.etapa],
      numero(metas.kcal),
      numero(metas.proteinaG),
      numero(metas.carbohidratosG),
      numero(metas.grasaG),
      numero(metas.ayunoHoras),
      texto(d.dia?.nota),
    ]);
  }
  return { nombre: 'Totales por día', filas };
}

// ---------------------------------------------------------------------------
// Ayunos y etapas (Req. 28.1)
// ---------------------------------------------------------------------------

const MS_POR_HORA = 3_600_000;

export interface AyunoParaExcel {
  ayuno: Fast;
  /** La meta que tenía ese día, si el ayuno no trae la suya. */
  metaHoras: number | null;
}

export function hojaDeAyunos(ayunos: readonly AyunoParaExcel[]): HojaDeExcel {
  const filas: Celda[][] = [
    ['Día', 'Inicio', 'Fin', 'Horas', 'Meta (h)', 'Cumplió la meta', 'De dónde salió', 'Correcciones', 'Nota'],
  ];
  for (const { ayuno, metaHoras } of ayunos) {
    const inicio = Date.parse(ayuno.inicio);
    const fin = ayuno.fin === null ? null : Date.parse(ayuno.fin);
    const horas = fin === null || Number.isNaN(inicio) || Number.isNaN(fin) ? null : (fin - inicio) / MS_POR_HORA;
    const meta = ayuno.metaHoras ?? metaHoras;
    filas.push([
      fechaLocalDe(ayuno.fin ?? ayuno.inicio),
      fechaYHora(ayuno.inicio),
      fechaYHora(ayuno.fin),
      numero(horas),
      numero(meta),
      horas === null || meta === null ? null : si(horas >= meta),
      ETIQUETA_ORIGEN_AYUNO[ayuno.origen] ?? ayuno.origen,
      ayuno.historial.length,
      texto(ayuno.nota),
    ]);
  }
  return { nombre: 'Ayunos', filas };
}

export interface EtapaParaExcel {
  etapa: Stage;
  plan: Plan | undefined;
}

/** Cómo se definió la meta de kcal de una etapa, en palabras y en número. */
export function metaDeEtapa(etapa: Stage): { modo: string; valor: number | null } {
  const m = etapa.metaKcal;
  if (m === null) return { modo: 'Igual a tu mantenimiento', valor: null };
  if (m.modo === 'fija') return { modo: 'Fija', valor: numero(m.kcal) };
  if (m.modo === 'diferencia') return { modo: 'Diferencia contra el mantenimiento', valor: numero(m.kcal) };
  return { modo: 'Porcentaje del mantenimiento', valor: numero(m.pct) };
}

export function hojaDeEtapas(etapas: readonly EtapaParaExcel[]): HojaDeExcel {
  const filas: Celda[][] = [
    [
      'Plan',
      'Ciclo',
      'Etapa',
      'Tipo',
      'Desde',
      'Hasta',
      'Cómo se define la meta',
      'Valor de la meta',
      'Proteína (g)',
      'Carbohidratos (g)',
      'Grasa (g)',
      'Ayuno (h)',
    ],
  ];
  for (const { etapa, plan } of etapas) {
    const meta = metaDeEtapa(etapa);
    filas.push([
      plan?.nombre ?? 'Plan que ya no existe',
      etapa.ciclo,
      etapa.indice + 1,
      ETIQUETA_TIPO_ETAPA[etapa.tipo] ?? etapa.tipo,
      etapa.fechaInicio,
      etapa.fechaFin,
      meta.modo,
      meta.valor,
      numero(etapa.proteinaG),
      numero(etapa.carbohidratosG),
      numero(etapa.grasaG),
      numero(etapa.ayunoHoras),
    ]);
  }
  return { nombre: 'Etapas', filas };
}

// ---------------------------------------------------------------------------
// El archivo completo
// ---------------------------------------------------------------------------

export interface DatosParaExcel {
  series: readonly SerieParaExcel[];
  pesajes: readonly PesajeParaExcel[];
  medidas: readonly Measurement[];
  comidas: readonly MealEntry[];
  dias: readonly DiaParaExcel[];
  ayunos: readonly AyunoParaExcel[];
  etapas: readonly EtapaParaExcel[];
}

/** Las siete hojas, en el orden en que se leen (Req. 28.1). */
export function hojasDeExcel(datos: DatosParaExcel, unidades: UnidadesDeExcel): HojaDeExcel[] {
  return [
    hojaDeEntrenamientos(datos.series, unidades),
    hojaDePesajes(datos.pesajes, unidades),
    hojaDeMedidas(datos.medidas, unidades),
    hojaDeComidas(datos.comidas),
    hojaDeTotales(datos.dias),
    hojaDeAyunos(datos.ayunos),
    hojaDeEtapas(datos.etapas),
  ];
}

/** "duofit-2026-10-30-0845.xlsx" */
export function nombreDeArchivoExcel(ahora: Date): string {
  const p2 = (n: number) => String(n).padStart(2, '0');
  const fecha = `${ahora.getFullYear()}-${p2(ahora.getMonth() + 1)}-${p2(ahora.getDate())}`;
  return `duofit-${fecha}-${p2(ahora.getHours())}${p2(ahora.getMinutes())}.xlsx`;
}

/** Cuántas filas de datos lleva cada hoja, para decirlo antes de entregar el archivo. */
export function resumenDeHojas(hojas: readonly HojaDeExcel[]): { nombre: string; filas: number }[] {
  return hojas.map((h) => ({ nombre: h.nombre, filas: Math.max(0, h.filas.length - 1) }));
}
